import { Component, ElementRef, AfterViewInit, ViewChild, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import SignaturePad from 'signature_pad';
import { PortalService } from '../../../../../../services/portal.service';
import { ChangeDetectorRef } from '@angular/core';
import { EnrollmentFormService } from '../../../../../../services/EnrollmentFormService';
import { EnrollmentComponent } from '../enrollment/enrollment.component';
import { environment } from '../../../../../../environments/environment';
import { fileUpload } from '../../../../../helpers/fileUpload.validator';
import { maxFileUploadSize } from '../../../../../helpers/maxFileUploadSize.validator';
import { TelemetryService } from '../../../../../../services/telemetry.service';

@Component({
  selector: 'app-insurance-info',
  templateUrl: './insurance-info.component.html',
  styleUrls: ['./insurance-info.component.scss'],
})
export class InsuranceInfoComponent implements OnInit, AfterViewInit {
  @ViewChild('canvas', { static: false }) signaturePadElement!: ElementRef<HTMLCanvasElement>;
  signaturePad!: SignaturePad;
  insuranceForm!: FormGroup;

  // Files selected for upload
  selectedFiles: {
    file: File;
    preview: string;
    description: string;
    name: string;
    type: string;
    attachmentId?: number;
  }[] = [];
  selectedSignatures: {
    file: File;
    preview: string;
    description: string;
    name: string;
    type: string;
    attachmentId?: number;
  }[] = [];

  _isSignatureAccepted: boolean = false;
  isFileUploading: boolean = false;
  isLoading: boolean = false;
  isInsuranceDocumentTouched: boolean = false;

  get isSignatureAccepted(): boolean{
    return this._isSignatureAccepted;
  }

  set isSignatureAccepted(value: boolean){
    this._isSignatureAccepted = value;
    if(value)
      this.updateSelectedSignatures();
  }

  updateSelectedSignatures(){
    this.selectedSignatures = [];
    this.portalService.getEnrollmentAttachment(this.enrollmentId).subscribe({
      next: (data: any) => {
        const signature = data.find((attachment: any) => attachment.enrollmentAttachmentTypeId === 8);
        if (signature) {
          let name = signature.filePath.split('/').pop();
          let type = signature.filePath.split('.').pop();
          this.selectedSignatures.push({
            file: new File([], name),
            preview: 'data:image/png;base64,' + signature.fileDocumentBase64Content,
            name,
            type: type === "pdf" ? "pdf" : "image",
            description: signature.description,
            attachmentId: signature.attachmentId,
          });
        }
      },
      error: (error: any) => {
        console.error('Error fetching attachments:', error);
      }
    }); 
  }

  enrollmentId: any;
  manufacturerId = environment.manufacturerID;

  // Updated Text
  insuranceCoverageText = "Do you have insurance or other prescription drug coverage? *";
  applicableText = "Please select the applicable insurance options below: *";
  merckProgramText = `By enrolling in the Merck Patient Assistance Program, I understand that I must meet eligibility requirements, including financial and medical criteria, as determined by the program.`;
  attestationText = "I certify that without the support of the Merck Patient Assistance Program, I would not be able to afford my prescribed Merck medication.";
  hardshipReasonText = "My financial and medical hardship is due to the following reasons: *";
  expirationNoticeText = `Note: The enrollment in the Merck Patient Assistance Program is subject to review and is valid for one year from the approval date. After one year, reapplication is required for continued eligibility.`;
  applicableCoverageText = "If applicable, please select the coverage options below:";
  medicaidDisclaimerText = `* I certify that I am not enrolled in any Medicaid, Medicare Part D, or any other government-funded insurance program for prescription medications.`;
  insuranceTypes: any;
  financialMedicalHardship: any;
  otherInsuranceTypeID = 10012;
  currentSequenceStep: string;
  editFlow = false;

  insuranceDocument = new FormControl('');
  insuranceSignature = new FormControl('');

  constructor(
    private router: Router,
    private portalService: PortalService,
    private cdr: ChangeDetectorRef,
    private efs: EnrollmentFormService,
    private activatedRoute: ActivatedRoute,
    private enrollmentComponent:EnrollmentComponent,
    private telemetryService: TelemetryService
  ) {
    this.insuranceForm = this.efs.enrollmentForm;
  }
  ngOnInit(): void {
    this.setSpecificContent();
    this.efs.enrollmentID$.subscribe((id) => {
      this.enrollmentId = id;
    });
    this.activatedRoute.paramMap.subscribe(params => {
      if (params.get('id')) {
        this.enrollmentId = params.get('id');
        this.editFlow = true;
      }
    });
    this.currentSequenceStep = localStorage.getItem('CurrentEnrollmentStep');
    this.efs.checkAndfetchEnrollmentDetails(this.enrollmentId);
    this.portalService.getProgramInsuranceType(environment.programID).subscribe(value => {
      this.insuranceTypes = value;
    });
    this.portalService.financialMedicalHardshipType().subscribe(value => {
      this.financialMedicalHardship = value;
    });

    if (this.isInsuranceSelectedControl) {
      this.insuranceDocument.setValidators([Validators.required]);
      this.insuranceSignature.setValidators([Validators.required]);
      this.insuranceDocument.updateValueAndValidity();
      this.insuranceSignature.updateValueAndValidity();
      // Default no options selected
      if (!this.editFlow && !this.efs.enrollmentInsuranceDTOs.length && !this.efs.finMediHardshipDTOs.length) this.isInsuranceSelectedControl.setValue(null);
      this.isInsuranceSelectedControl.valueChanges.subscribe((value) => {
        if (value) {
          this.cdr.detectChanges();
          this.initializeSignaturePad();
          
          this.efs.enrollmentInsuranceDTOs.setValidators([Validators.required, Validators.minLength(1)]);
          this.efs.finMediHardshipDTOs.setValidators([Validators.required, Validators.minLength(1)]);
          this.efs.enrollmentInsuranceDTOs.updateValueAndValidity();
          this.efs.finMediHardshipDTOs.updateValueAndValidity();
        } else {
          this.efs.enrollmentInsuranceDTOs.clear();
          this.efs.finMediHardshipDTOs.clear();

          if(this.selectedFiles.length){
            this.removeFile(0);
          }
          // if(this.isSignatureAccepted){
          //   this.clearSignature();
          // }
          this.efs.enrollmentInsuranceDTOs.clearValidators();
          this.efs.finMediHardshipDTOs.clearValidators();
          this.insuranceDocument.clearValidators();
          this.insuranceSignature.clearValidators();
          this.efs.enrollmentInsuranceDTOs.updateValueAndValidity();
          this.efs.finMediHardshipDTOs.updateValueAndValidity();
          this.insuranceDocument.updateValueAndValidity();
          this.insuranceSignature.updateValueAndValidity();
          this.insuranceForm.get('isMedicarePartBCoverage').setValue(false);
          this.insuranceForm.get('isMedicarePartDCoverage').setValue(false);
        }
      })
    }

    if (this.efs.currentEnrollmentObject?.enrollmentAttachmentViewDTOs?.length) {
      const document = this.efs.currentEnrollmentObject?.enrollmentAttachmentViewDTOs.find((attachment: any) => attachment.enrollmentAttachmentTypeId === 7);
      if (document) {
        this.insuranceDocument.setValue(document.attachmentId.toString());
        this.insuranceDocument.updateValueAndValidity();
        let name = document.filePath.split('/').pop();
        let type = document.filePath.split('.').pop();
        this.selectedFiles.push({
          file: new File([], name),
          preview: 'data:image/png;base64,' + document.fileDocumentBase64Content,
          name,
          type: type === "pdf" ? "pdf" : "image",
          description: document.description,
          attachmentId: document.attachmentId,
        });
      }
      const signature = this.efs.currentEnrollmentObject?.enrollmentAttachmentViewDTOs.find((attachment: any) => attachment.enrollmentAttachmentTypeId === 8);
      if (signature) {
        let name = signature.filePath.split('/').pop();
        let type = signature.filePath.split('.').pop();
        this.selectedSignatures.push({
          file: new File([], name),
          preview: 'data:image/png;base64,' + signature.fileDocumentBase64Content,
          name,
          type: type === "pdf" ? "pdf" : "image",
          description: signature.description,
          attachmentId: signature.attachmentId,
        });
      }
    }
  }

  get nextStep() {
    return this.efs.getNextStep();
  }

  get enrollmentInsuranceDTOs(): FormArray {
    return this.insuranceForm.get('enrollmentInsuranceDTOs') as FormArray;
  }

  get finMediHardshipDTOs(): FormArray {
    return this.insuranceForm.get('finMediHardshipDTOs') as FormArray;
  }

  get enrollmentOtherInsuranceDTOs(): FormGroup {
    let index = this.enrollmentInsuranceDTOs.value.findIndex((insurance: any) => insurance.insuranceTypeID === this.otherInsuranceTypeID)
    if (index !== -1) {
      return this.enrollmentInsuranceDTOs.at(index) as FormGroup;
    } else {
      return null;
    }
  }

  get isInsuranceSelectedControl(): FormControl {
    return this.insuranceForm.get('isInsuranceSelected') as FormControl;
  }

  get isFormInvalid() {
    // is true btn disabled
    let result = false;
    const fields = ['isInsuranceSelected', 'enrollmentInsuranceDTOs', 'finMediHardshipDTOs'];
    fields.forEach((field) => {
      if(this.insuranceForm.get(field).invalid) result = true;
    });
    if(this.insuranceDocument.invalid) result = true;
    if(this.insuranceSignature.invalid) result = true;
    return result;
  } 

  onInsuranceTypeChange(insuranceTypeID: number, isSelected: boolean): void {
    if (insuranceTypeID === 0) {
      console.warn('Invalid insuranceTypeID: 0. Operation skipped.');
      return;
    }
    this.efs.enrollmentInsuranceDTOs.markAsTouched();
    const index = this.efs.enrollmentInsuranceDTOs.controls.findIndex(
      (control) => control.value.insuranceTypeID === insuranceTypeID
    );

    if (isSelected) {
      if (index === -1) {
        this.efs.addInsuranceEntry({
          insuranceTypeID,
          insuranceCompanyName: '', // Default value
          memberID: 0, // Default value
        });
      }
    } else {
      if (index !== -1) {
        this.efs.removeInsuranceEntry(index);
      }
    }

  }

  isInsuranceTypeSelected(insuranceTypeID: number): boolean {
    return this.efs.enrollmentInsuranceDTOs.controls.some(
      (control) => control.value.insuranceTypeID === insuranceTypeID
    );
  }

  onMedicalHardshipChange(finMediHardshipTypeID: number, isSelected: boolean): void {
    this.efs.finMediHardshipDTOs.markAsTouched();
    const existingIndex = this.efs.finMediHardshipDTOs.controls.findIndex(
      (control) => control.value.finMediHardshipTypeID === finMediHardshipTypeID
    );

    if (isSelected && existingIndex === -1) {
      this.efs.addfinMediHardshipEntry({
        finMediHardshipTypeID
      });
    } else if (!isSelected && existingIndex !== -1) {
      this.efs.removefinMediHardshipDTOs(existingIndex);
    }

  }

  isFinHardshipTypeSelected(finMediHardshipTypeID: number): boolean {
    return this.efs.finMediHardshipDTOs.controls.some(
      (control) => control.value.finMediHardshipTypeID === finMediHardshipTypeID
    );
  }

  ngAfterViewInit(): void {
    this.initializeSignaturePad();
  }

  private initializeSignaturePad(): void {
    if (this.signaturePadElement?.nativeElement) {
      this.signaturePad = new SignaturePad(this.signaturePadElement.nativeElement, {
        minWidth:0.5,
        maxWidth: 1,
        penColor: 'rgb(0, 0, 0)',
      });

      if(this.efs.currentEnrollmentObject?.enrollmentAttachmentViewDTOs?.length){
        const sign = this.efs.currentEnrollmentObject?.enrollmentAttachmentViewDTOs.find((attachment: any) => attachment.enrollmentAttachmentTypeId === 8);
        if (sign) {
          this.insuranceSignature.setValue("1");
          this.insuranceSignature.markAsTouched();
          this.insuranceSignature.updateValueAndValidity();
          const base64Sign = 'data:image/png;base64,' + sign.fileDocumentBase64Content
          this.insuranceForm.patchValue({ signature: base64Sign });
          this.signaturePad.fromDataURL(base64Sign);
          this.isSignatureAccepted = true;
          this.signaturePad.off();
        }
      }
    } else {
      console.error('Canvas element not found for SignaturePad initialization');
    }
  }

  clearSignature(): void {
    this.telemetryService.logEvent('Clear Signature Clicked', {
      step: 'Insurance',
      timestamp: new Date().toISOString(),
    });
    this.removeSignature(0);
  }

  acceptSignature(): void {
    if (!this.signaturePad.isEmpty()) {
      this.telemetryService.logEvent('Accept Signature Clicked', {
        step: 'Insurance',
        timestamp: new Date().toISOString(),
      });
      const base64Signature = this.signaturePad.toDataURL();
      this.insuranceForm.patchValue({ signature: base64Signature });

      const signatureBlob = this.base64ToBlob(base64Signature);
      const formData = new FormData();
      formData.append('EnrollmentId', this.enrollmentId.toString());
      formData.append('ManufacturerId', this.manufacturerId.toString());
      formData.append('EnrollmentAttachmentDocumentDTOs[0].EnrollmentAttchmentTypeId', '8');
      formData.append('EnrollmentAttachmentDocumentDTOs[0].Description', `Insurance_Signature`);
      formData.append('EnrollmentAttachmentDocumentDTOs[0].FileDocument', signatureBlob, `insurance_signature.png`);

      this.portalService.uploadDocumentAttachment(formData).subscribe({
        next: (response) => {
          this.insuranceSignature.setValue("1");
          this.insuranceSignature.markAsTouched();
          this.insuranceSignature.updateValueAndValidity();
          this.isSignatureAccepted = true;
          this.signaturePad.off(); // Disable drawing on the signature pad
        },
        error: (error) => {
          console.error('Error uploading signature:', error);
        }
      });
    } else {
      console.log('Signature pad is empty.');
    }
  }

  base64ToBlob(base64: string): Blob {
    const byteString = atob(base64.split(',')[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const intArray = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      intArray[i] = byteString.charCodeAt(i);
    }
    return new Blob([intArray], { type: 'image/png' });
  }
  setSpecificContent(): void {
    this.portalService.getDataByConsentType('Insurance Attestation Header').subscribe(data => {
      if (data.length) this.merckProgramText = data[0].contentText;
    });

    this.portalService.getDataByConsentType('Insurance Attestation Body1').subscribe(data => {
      if (data.length) this.expirationNoticeText = data[0].contentText;
    });

    this.portalService.getDataByConsentType('Insurance Attestation Body2').subscribe(data => {
      if (data.length) this.medicaidDisclaimerText = data[0].contentText;
    });
  }

  onSave(isSaveAndExit = false): void {
    if(!isSaveAndExit) {
			this.telemetryService.logEvent('Next Button Clicked', {
				step: this.nextStep.enrollmentStepName,
				timestamp: new Date().toISOString(),
			});
		}

    this.isLoading = true;
    this.insuranceForm.patchValue({
      lastEnrollmentStep: this.currentSequenceStep
    });
    if (this.insuranceForm.value) {
      const eid = this.insuranceForm.get('enrollmentId')?.value || this.enrollmentId;
      this.portalService.createEnrollment(this.insuranceForm.value, eid).subscribe((value: any) => {
        this.isLoading = false;
        this.efs.patchEnrollmentForm(value);
        this.insuranceForm.patchValue({
          enrollmentId: value.enrollmentId
        });
        if (isSaveAndExit) {
          this.efs.resetForm();
          this.router.navigate(['/submissions'], { relativeTo: this.activatedRoute });
        } else {
          if (this.editFlow) {
						this.router.navigate([`../${this.nextStep.enrollmentRoute}`], { relativeTo: this.activatedRoute });
						//localStorage.setItem('CurrentEnrollmentStep', (this.nextStep.sequenceNumber).toString());
						this.enrollmentComponent.setCurrentStep(this.nextStep.enrollmentStepName,'true');
          } else {
            this.enrollmentComponent.setCurrentStep(this.nextStep.enrollmentStepName);
          }
        }
      });
    } else {
      const enrollmentInsuranceDTOs = this.insuranceForm.get('enrollmentInsuranceDTOs').value;
    }
  }

  onFilesSelected(event: any): void {
    const files: FileList = event.target.files;
    this.insuranceDocument.clearValidators();
    this.insuranceDocument.setValidators([
      Validators.required,
      fileUpload(files),
      maxFileUploadSize(files)
    ]);
    this.insuranceDocument.updateValueAndValidity();

    if (!this.insuranceDocument?.errors?.['maxFileUploadSize'] && !this.insuranceDocument?.errors?.['fileUpload'] && files.length > 0) {
      const file = files[0]; // Only allow the first file

      // Check for duplicate file
      if (this.selectedFiles.some((selected) => selected.file.name === file.name)) {
        console.warn(`File "${file.name}" is already selected.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const description = `Insurance_Card`;

        // Add file to selectedFiles array
        this.selectedFiles.push({
          file,
          preview: e.target.result,
          name: file.name,
          type: file.type.startsWith('image') ? 'image' : 'pdf',
          description,
        });


        // Disable the file input
        const fileInput = document.getElementById('fileUpload') as HTMLInputElement;
        if (fileInput) {
          fileInput.disabled = true;
        }

        // Trigger file upload
        this.uploadFile(file, description);
      };
      reader.readAsDataURL(file);
    }
  }

  uploadFile(file: File, description: string): void {
    const formData = new FormData();
    formData.append('EnrollmentId', this.enrollmentId.toString());
    formData.append('ManufacturerId', this.manufacturerId.toString());
    formData.append('EnrollmentAttachmentDocumentDTOs[0].EnrollmentAttchmentTypeId', '7');
    formData.append('EnrollmentAttachmentDocumentDTOs[0].Description', description);
    formData.append('EnrollmentAttachmentDocumentDTOs[0].FileDocument', file, file.name);

    this.isFileUploading = true;

    this.portalService.uploadDocumentAttachment(formData).subscribe({
      next: (response) => {
        if (Array.isArray(response) && response.length > 0) {
          const documentAttachmentId = response[0].id;
          const uploadedFile = this.selectedFiles.find((f) => f.description === description);
          if (uploadedFile) {
            this.insuranceDocument.setValue(documentAttachmentId);
            this.insuranceDocument.markAsTouched();
            this.insuranceDocument.updateValueAndValidity();
            uploadedFile.attachmentId = documentAttachmentId;
            this.cdr.detectChanges();
          }
        }
        this.isFileUploading = false;
      },
      error: (error) => {
        console.error('Error uploading file:', error);
        this.isFileUploading = false;
      }
    });
  }

  removeFile(index: number): void {
    const fileToRemove = this.selectedFiles[index];
    this.selectedFiles.splice(index, 1);
    this.insuranceDocument.setValue('');
    this.insuranceDocument.markAsTouched();
    this.insuranceDocument.updateValueAndValidity();

    // Re-enable the file input
    const fileInput = document.getElementById('fileUpload') as HTMLInputElement;
    if (fileInput) {
      fileInput.disabled = false;
      fileInput.value = '';
    }

    if (!fileToRemove.attachmentId) {
      console.error('Attachment ID not found for the file:', fileToRemove);
      return;
    }

    this.portalService.removeDocumentAttachment(fileToRemove.attachmentId).subscribe({
      next: (response) => {

        // Trigger change detection
        this.cdr.detectChanges();

        // Reset upload container if no files remain
        if (this.selectedFiles.length === 0) {
          console.log('No files left. Resetting upload container.');
        }
      },
      error : (error:any)=> {
        console.error('Error removing document:', error);
      }
    });
  }

  removeSignature(index: number): void {
    const fileToRemove = this.selectedSignatures[index];

    this.selectedSignatures.splice(index, 1);

    if(fileToRemove){
      if (!fileToRemove.attachmentId) {
        console.error('Attachment ID not found for the file:', fileToRemove);
        return;
      }

      this.portalService.removeDocumentAttachment(fileToRemove.attachmentId).subscribe({
        next: (response) => {
          this.signaturePad.clear();
          this.signaturePad.on();
          this.isSignatureAccepted = false;
          this.insuranceForm.patchValue({ signature: '' });
          this.insuranceSignature.setValue("");
          this.insuranceSignature.markAsTouched();
          this.insuranceSignature.updateValueAndValidity();

          // Trigger change detection
          this.cdr.detectChanges();

        },
        error: (error: any) => {
          console.error('Error removing document:', error);
        }
      });
    }
    else{
      this.signaturePad.clear();
      this.signaturePad.on();
      this.isSignatureAccepted = false;
      this.insuranceForm.patchValue({ signature: '' });
      this.insuranceSignature.setValue("");
      this.insuranceSignature.markAsTouched();
      this.insuranceSignature.updateValueAndValidity();

      // Trigger change detection
      this.cdr.detectChanges();
    }
  }
}
